import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import orderAdminApi from '../../services/orderAdminApi';
import { usePermissions } from '../../hooks/usePermissions';
import InvoicePreviewModal from '../common/InvoicePreviewModal';
import {
    Search,
    Download,
    Eye,
    Loader,
    Calendar,
    Package,
    MapPin,
    Phone,
    Mail,
    IndianRupee,
    ArchiveRestore,
    ThumbsDown,
    Receipt,
    MoreVertical,
    X,
    Check,
    Truck,
    Clock,
    ThumbsUp,
    User,
    Store,
    Loader2,
    ShieldCheck,
    RefreshCw,
    ArrowLeftRight,
    AlertTriangle,
    CalendarDays,
    ShoppingCart,
    BadgeInfo,
    UserPlus,
    UserCog,
    Users,
    Hash,
    CreditCard,
    Navigation,
    Ticket,
    Star,
    MessageSquare,
    Copy,
    CheckCheck,
    ArrowLeft,
    ExternalLink
} from 'lucide-react';


//page
const ORDERS_PER_PAGE = 10;

const NON_CANCELLABLE_STATUSES = [
    'out_for_delivery',
    'at_delivery_point',
    'delivered',
    'picked',
    'cancelled',
    'refund_initiated',
    'refund_processing',
    'refund_completed',
    'refund_failed',
    'user_rejected',
    'shop_rejected'
];

const canCancelAndRefundOrder = (order) => {
    if (!order) return false;
    return order.paymentStatus === 'completed' && !NON_CANCELLABLE_STATUSES.includes(order.status);
};

const canRetryRefundOrder = (order) => {
    if (!order) return false;
    return order.paymentStatus === 'completed' && order.status === 'refund_failed';
};

const isInsufficientBalanceFailure = (order) => {
    return order?.refundDetails?.failureReason === 'insufficient_balance'
        || (order?.refundDetails?.error?.description || '').toLowerCase().includes('enough balance');
};

const SHOP_MEMBER_ASSIGNED_STATUSES = new Set(['confirmed', 'updated']);

const resolveRefId = (ref) => {
    if (ref == null) return null;
    if (typeof ref === 'string' || typeof ref === 'number') return String(ref);
    if (ref._id != null) return String(ref._id);
    if (ref.id != null) return String(ref.id);
    return null;
};

const getOrderShopId = (order) => resolveRefId(order?.shopId);

const getDeliveryPartnerId = (order) => resolveRefId(order?.deliveryBoy);

const getShopMemberRefId = (member) => resolveRefId(member) || resolveRefId(member?.userId);

const extractOrderFromSearchResponse = (payload) => {
    if (!payload) return null;
    if (payload.orders?.[0]) return payload.orders[0];
    if (payload.data?.orders?.[0]) return payload.data.orders[0];
    if (payload.order) return payload.order;
    if (payload.data?.order) return payload.data.order;
    if (payload.data?.orderId || payload.data?._id) return payload.data;
    return null;
};

const extractDeliveryBoyEntries = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.deliveryBoys)) return payload.deliveryBoys;
    return [];
};

const isOrderTerminal = (order) => NON_CANCELLABLE_STATUSES.includes(order?.status);

const canAssignShopMember = (order) => {
    if (!getOrderShopId(order) || isOrderTerminal(order)) return false;
    return !(order.shopMembers || []).some((member) => getShopMemberRefId(member));
};

const canReassignShopMember = (order) => {
    if (!getOrderShopId(order) || isOrderTerminal(order)) return false;
    const assignedMember = (order.shopMembers || []).find((member) => getShopMemberRefId(member));
    if (!assignedMember) return false;
    return SHOP_MEMBER_ASSIGNED_STATUSES.has(assignedMember.status);
};

const canAssignDeliveryBoy = (order) => {
    if (!order || order.orderType !== 'delivery' || isOrderTerminal(order)) return false;
    return !getDeliveryPartnerId(order);
};

const canReassignDeliveryBoy = (order) => {
    if (!order || order.orderType !== 'delivery' || isOrderTerminal(order)) return false;
    return Boolean(getDeliveryPartnerId(order));
};

const canDownloadInvoice = (order) => ['delivered', 'picked'].includes(order?.status);

const toMoneyNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const sumItemMoneyField = (items, field) =>
    (Array.isArray(items) ? items : []).reduce((sum, item) => sum + toMoneyNumber(item?.[field]), 0);

const getSettlementBreakdown = (order) => {
    const items = order?.items || [];
    const orderValue = toMoneyNumber(order?.updatedGrandTotal ?? order?.grandTotal);
    const gateway = order?.paymentGatewayCharges != null
        ? toMoneyNumber(order.paymentGatewayCharges)
        : sumItemMoneyField(items, 'paymentGatewayCharges');
    const commission = order?.commission != null
        ? toMoneyNumber(order.commission)
        : sumItemMoneyField(items, 'commission');
    const earnings = order?.totalEarnings != null
        ? toMoneyNumber(order.totalEarnings)
        : orderValue - gateway - commission;
    return { orderValue, gateway, commission, earnings };
};

const formatDateShort = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
    });
};

const extractInvoiceHtml = (payload) => {
    if (typeof payload === 'string') return payload;
    if (typeof payload?.data === 'string') return payload.data;
    if (typeof payload?.html === 'string') return payload.html;
    if (typeof payload?.data?.html === 'string') return payload.data.html;
    return '';
};

/** Hide items marked unavailable / out of stock in the Order items list. */
const isOutOfStockOrderItem = (item) => {
    if (!item) return false;
    const status = String(item.availabilityStatus || '').trim().toLowerCase();
    const notes = String(item.shopNotes || '').trim().toLowerCase();
    if (status === 'unavailable') return true;
    if (notes.includes('out of stock')) return true;
    return false;
};

const getVisibleOrderItems = (items) =>
    (Array.isArray(items) ? items : []).filter((item) => !isOutOfStockOrderItem(item));


const formatDate = (dateString) => {
    if (!dateString) return null;
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata',
        hour12: false
    };
    return new Date(dateString).toLocaleString('en-IN', options);
};

const formatCurrency = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount) || amount === 0) return null;
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
    }).format(amount);
};

/** Always formats a money value, including ₹0.00 */
const formatMoney = (amount) => {
    const n = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
    }).format(n);
};

const displayText = (value, fallback = '—') => {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
};

const formatStatusLabel = (value) => {
    if (!value) return '—';
    return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getPaymentStatusColor = (status) => {
    const colors = {
        completed: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
        pending: 'bg-yellow-100 text-yellow-800',
        refunded: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
};

const getRefundStatusColor = (status) => {
    const colors = {
        not_initiated: 'bg-gray-100 text-gray-700',
        initiated: 'bg-yellow-100 text-yellow-800',
        processing: 'bg-blue-100 text-blue-800',
        completed: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
};

const MetaRow = ({ label, value, mono = false }) => (
    <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 sm:gap-4 py-2 border-b border-slate-100 last:border-0">
        <dt className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">{label}</dt>
        <dd className={`text-sm text-slate-800 text-left sm:text-right break-all ${mono ? 'font-mono text-xs' : 'font-medium'}`}>{value}</dd>
    </div>
);

const DetailSection = ({ title, icon: Icon, children, className = '', badge = null }) => (
    <section className={`rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden ${className}`}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
            <h3 className="flex items-center text-sm font-semibold text-slate-800">
                {Icon && (
                    <span className="mr-2 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-600">
                        <Icon size={15} />
                    </span>
                )}
                {title}
            </h3>
            {badge}
        </div>
        <div className="p-4">{children}</div>
    </section>
);

const StatusPill = ({ label, tone = 'neutral' }) => {
    const tones = {
        success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
        danger: 'bg-rose-50 text-rose-700 ring-rose-200',
        warning: 'bg-amber-50 text-amber-700 ring-amber-200',
        info: 'bg-sky-50 text-sky-700 ring-sky-200',
        neutral: 'bg-slate-100 text-slate-600 ring-slate-200'
    };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${tones[tone] || tones.neutral}`}>
            {label}
        </span>
    );
};

const EmptyHint = ({ children = 'Not available' }) => (
    <p className="text-sm text-slate-400 italic py-1">{children}</p>
);

const CopyButton = ({ text, label = 'Copy', className = '' }) => {
    const [copied, setCopied] = useState(false);
    if (!text) return null;
    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(String(text));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* ignore */
        }
    };
    return (
        <button
            type="button"
            onClick={handleCopy}
            title={label ? `Copy ${label}` : 'Copy'}
            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors ${className}`}
        >
            {copied ? <CheckCheck size={12} className="text-emerald-600" /> : <Copy size={12} />}
            {copied ? 'Copied' : (label || null)}
        </button>
    );
};

const ContactLink = ({ phone, className = '' }) => {
    if (!phone) return <span className="text-slate-400">—</span>;
    return (
        <a
            href={`tel:${phone}`}
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center gap-1.5 font-semibold text-sky-700 hover:text-sky-900 ${className}`}
        >
            <Phone size={13} />
            {phone}
        </a>
    );
};

const BoolText = ({ value }) => {
    if (value === true) return <StatusPill label="Yes" tone="success" />;
    if (value === false) return <StatusPill label="No" tone="neutral" />;
    return '—';
};

const paymentTone = (status) => {
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'danger';
    if (status === 'pending') return 'warning';
    return 'neutral';
};

const refundTone = (status) => {
    if (status === 'successful' || status === 'completed') return 'success';
    if (status === 'failed') return 'danger';
    if (status === 'initiated' || status === 'processing') return 'info';
    return 'neutral';
};

/** Human labels for common payment/refund detail keys */
const DETAIL_FIELD_LABELS = {
    razorpay_order_id: 'Razorpay Order ID',
    razorpay_payment_id: 'Razorpay Payment ID',
    razorpay_signature: 'Signature',
    webhook_verified: 'Webhook Verified',
    capturedAt: 'Captured At',
    refund_id: 'Refund ID',
    razorpay_refund_id: 'Razorpay Refund ID',
    amount: 'Amount',
    currency: 'Currency',
    failureReason: 'Failure Reason',
    error: 'Error',
    description: 'Description',
    status: 'Status',
    id: 'ID',
    speed: 'Speed',
    created_at: 'Created At'
};

const formatDetailValue = (key, value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
        if (key.toLowerCase().includes('amount')) return formatMoney(value);
        return String(value);
    }
    if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDate(value) || value;
        return value;
    }
    if (typeof value === 'object') {
        // Flatten one level for nested error objects
        if (value.description || value.reason || value.code) {
            return [value.code, value.reason, value.description].filter(Boolean).join(' — ');
        }
        return null; // skip deep nested objects
    }
    return String(value);
};

const ObjectFields = ({ data, emptyLabel = 'No details' }) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return <EmptyHint>{emptyLabel}</EmptyHint>;
    }
    const entries = Object.entries(data)
        .map(([key, value]) => {
            const formatted = formatDetailValue(key, value);
            if (formatted === null) return null;
            return {
                key,
                label: DETAIL_FIELD_LABELS[key] || formatStatusLabel(key),
                value: formatted,
                mono: /id|signature|order/i.test(key)
            };
        })
        .filter(Boolean);

    if (!entries.length) return <EmptyHint>{emptyLabel}</EmptyHint>;

    return (
        <dl className="space-y-0">
            {entries.map((entry) => (
                <MetaRow key={entry.key} label={entry.label} value={entry.value} mono={entry.mono} />
            ))}
        </dl>
    );
};

const hasLocationData = (location) => {
    if (!location) return false;
    return location.latitude != null || location.longitude != null || location.address || location.lastUpdated;
};

const LocationCard = ({ title, location }) => {
    if (!hasLocationData(location)) {
        return (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-3 py-3">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{title}</p>
                <EmptyHint>Location not recorded</EmptyHint>
            </div>
        );
    }

    const mapsUrl =
        location.latitude != null && location.longitude != null
            ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}`
            : null;

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50/40 px-3 py-3">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{title}</p>
            {location.address && <p className="text-sm text-slate-800 font-medium mb-2">{location.address}</p>}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                <span>Lat: <span className="font-medium text-slate-800">{displayText(location.latitude)}</span></span>
                <span>Lng: <span className="font-medium text-slate-800">{displayText(location.longitude)}</span></span>
            </div>
            {location.lastUpdated && (
                <p className="text-xs text-slate-400 mt-2">Updated {formatDate(location.lastUpdated)}</p>
            )}
            {mapsUrl && (
                <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center mt-2 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                >
                    <Navigation size={12} className="mr-1" />
                    View on Maps
                </a>
            )}
        </div>
    );
};

const OtpCard = ({ title, otp }) => {
    if (!otp || (!otp.code && !otp.generatedAt)) {
        return (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-3 py-3">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{title}</p>
                <EmptyHint>OTP not generated</EmptyHint>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-3">
            <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{title}</p>
                <StatusPill label={otp.isUsed ? 'Used' : 'Active'} tone={otp.isUsed ? 'neutral' : 'success'} />
            </div>
            <p className="text-2xl font-bold tracking-[0.2em] text-slate-900 font-mono">{displayText(otp.code)}</p>
            <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-slate-500">
                <span>Generated: {displayText(formatDate(otp.generatedAt))}</span>
                <span>Expires: {displayText(formatDate(otp.expiresAt))}</span>
                <span>Used at: {displayText(formatDate(otp.usedAt))}</span>
            </div>
        </div>
    );
};

const ProofOfDeliveryCard = ({ proof }) => {
    if (!proof) return <EmptyHint>No proof of delivery</EmptyHint>;
    const received = proof.receivedBy || {};
    const photos = proof.photos || [];
    const hasAny = photos.length || proof.customerSignature || received.name || received.relation || received.contactNumber;
    if (!hasAny) return <EmptyHint>No proof of delivery yet</EmptyHint>;

    return (
        <div className="space-y-3">
            <dl>
                <MetaRow label="Received By" value={displayText(received.name)} />
                <MetaRow label="Relation" value={displayText(received.relation)} />
                <MetaRow label="Contact" value={displayText(received.contactNumber)} />
            </dl>
            {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {photos.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                            <img src={url} alt={`POD ${i + 1}`} className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                        </a>
                    ))}
                </div>
            )}
            {proof.customerSignature && (
                <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Signature</p>
                    <img src={proof.customerSignature} alt="Customer signature" className="max-h-24 rounded border border-slate-200 bg-white" />
                </div>
            )}
        </div>
    );
};

const DeliveryAttemptsList = ({ attempts }) => {
    if (!attempts?.length) return <EmptyHint>No delivery attempts</EmptyHint>;
    return (
        <div className="space-y-2">
            {attempts.map((attempt, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-800">Attempt #{displayText(attempt.attemptNumber, idx + 1)}</span>
                        <span className="text-xs text-slate-500">{displayText(formatDate(attempt.attemptedAt))}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{displayText(attempt.failureReason, 'No failure reason')}</p>
                    {attempt.nextAttemptScheduled && (
                        <p className="text-xs text-slate-400 mt-1">Next: {formatDate(attempt.nextAttemptScheduled)}</p>
                    )}
                </div>
            ))}
        </div>
    );
};

const AddressFields = ({ address, emptyLabel = 'No address mentioned' }) => {
    if (!address) {
        return <p className="text-gray-400 italic text-sm">{emptyLabel}</p>;
    }

    const hasLine =
        address.buildingNo || address.street || address.floor || address.landmark ||
        address.city || address.state || address.pincode;
    const hasCoords = address.latitude != null || address.longitude != null;
    const receiver = address.receiverDetails;
    const hasReceiver = receiver && (receiver.name || receiver.phone);

    if (!hasLine && !hasCoords && !hasReceiver && !address.name && !address.contactPhone && !address.alternatePhone) {
        return <p className="text-gray-400 italic text-sm">{emptyLabel}</p>;
    }

    return (
        <div className="space-y-1 text-sm text-gray-700">
            {address.name && <p className="font-medium text-gray-900">{address.name}</p>}
            {(address.buildingNo || address.floor || address.street) && (
                <p>
                    {address.buildingNo && `${address.buildingNo}`}
                    {address.buildingNo && (address.floor || address.street) ? ', ' : ''}
                    {address.floor && `Floor ${address.floor}`}
                    {address.floor && address.street ? ', ' : ''}
                    {address.street}
                </p>
            )}
            {address.landmark && <p>Landmark: {address.landmark}</p>}
            {(address.city || address.state || address.pincode) && (
                <p>
                    {[address.city, address.state].filter(Boolean).join(', ')}
                    {(address.city || address.state) && address.pincode ? ' - ' : ''}
                    {address.pincode}
                </p>
            )}
            {address.contactPhone && (
                <p className="flex items-center text-gray-600">
                    <Phone className="mr-1.5 shrink-0" size={12} />
                    {address.contactPhone}
                </p>
            )}
            {address.alternatePhone && (
                <p className="text-gray-500 text-xs">Alt: {address.alternatePhone}</p>
            )}
            {hasReceiver && (
                <p className="text-gray-500 text-xs mt-1">
                    Receiver: {[receiver.name, receiver.phone].filter(Boolean).join(' • ') || '—'}
                </p>
            )}
            {hasCoords && (
                <p className="flex items-center text-xs text-gray-500 mt-1">
                    <Navigation className="mr-1.5 shrink-0" size={12} />
                    {displayText(address.latitude)} , {displayText(address.longitude)}
                </p>
            )}
        </div>
    );
};

const formatDisplayOrderId = (orderId) => {
    if (!orderId) return orderId;
    return String(orderId).replace(/^ORDER-ID-/i, '');
};

const getStatusIcon = (status) => {
    const icons = {
        pending_shop_confirmation: <Clock className="h-4 w-4" />,
        shop_confirmed: <ThumbsUp className="h-4 w-4" />,
        shop_rejected: <ThumbsDown className="h-4 w-4" />,
        stock_updated: <ArchiveRestore className="h-4 w-4" />,
        user_accepted: <Check className="h-4 w-4" />,
        user_rejected: <X className="h-4 w-4" />,
        payment_pending: <Clock className="h-4 w-4" />,
        payment_completed: <IndianRupee className="h-4 w-4" />,
        order_placed: <Store className="h-4 w-4" />,
        order_under_process: <Loader className="h-4 w-4 animate-spin" />,
        ready_for_pickup: <Package className="h-4 w-4" />,
        out_for_delivery: <Truck className="h-4 w-4" />,
        at_delivery_point: <Package className="h-4 w-4" />,
        delivered: <ShieldCheck className="h-4 w-4" />,
        picked: <Check className="h-4 w-4" />,
        cancelled: <X className="h-4 w-4" />,
        refund_initiated: <RefreshCw className="h-4 w-4" />,
        refund_processing: <ArrowLeftRight className="h-4 w-4 animate-pulse" />,
        refund_completed: <Check className="h-4 w-4" />,
        refund_failed: <AlertTriangle className="h-4 w-4 text-red-500" />
    };

    return icons[status] || <Package className="h-4 w-4" />;
};

const OrderStatusBadge = ({ status, size = 'sm' }) => {
    const isSmall = size === 'sm';
    const label = statusOptions[status] || status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN';

    return (
        <span
            className={`inline-flex items-center rounded-full ${getStatusColor(status)} ${
                isSmall ? 'px-2 py-1 text-xs font-semibold gap-1' : 'px-3 py-1.5 text-sm font-medium gap-1.5'
            }`}
        >
            {status === 'shop_confirmed' ? (
                <span
                    className={`inline-flex items-center justify-center rounded-full bg-blue-600/15 shrink-0 ${
                        isSmall ? 'w-4 h-4' : 'w-5 h-5'
                    }`}
                >
                    <ThumbsUp
                        className={isSmall ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}
                        strokeWidth={2.5}
                    />
                </span>
            ) : (
                <span className="inline-flex shrink-0 items-center">{getStatusIcon(status)}</span>
            )}
            <span className="leading-none">{label}</span>
        </span>
    );
};

const statusOptions = {
    pending_shop_confirmation: 'Pending Shop Confirmation',
    shop_confirmed: 'Shop Confirmed',
    shop_rejected: 'Out of stock',
    stock_updated: 'Stock Updated',
    user_accepted: 'User Accepted',
    user_rejected: 'User Rejected',
    payment_pending: 'Payment Pending',
    payment_not_completed: 'Payment Not Completed',
    payment_completed: 'Payment Completed',
    order_placed: 'Order Placed',
    order_under_process: 'Order Under Process',
    ready_for_pickup: 'Ready for Pickup',
    out_for_delivery: 'Out for Delivery',
    at_delivery_point: 'At Delivery Point',
    delivered: 'Delivered',
    picked: 'Picked Up',
    cancelled: 'Cancelled',
    refund_initiated: 'Refund Initiated',
    refund_processing: 'Refund Processing',
    refund_completed: 'Refund Completed',
    refund_failed: 'Refund Failed',
};

const getStatusColor = (status) => {
    const colors = {
        pending_shop_confirmation: 'bg-yellow-100 text-yellow-800',
        shop_confirmed: 'bg-blue-100 text-blue-800',
        shop_rejected: 'bg-red-100 text-red-800',
        stock_updated: 'bg-gray-100 text-gray-800',
        user_accepted: 'bg-green-100 text-green-800',
        user_rejected: 'bg-red-100 text-red-800',
        payment_pending: 'bg-yellow-100 text-yellow-800',
        payment_not_completed: 'bg-gray-100 text-gray-800',
        payment_completed: 'bg-green-100 text-green-800',
        order_placed: 'bg-blue-100 text-blue-800',
        order_under_process: 'bg-blue-100 text-blue-800',
        ready_for_pickup: 'bg-purple-100 text-purple-800',
        out_for_delivery: 'bg-indigo-100 text-indigo-800',
        at_delivery_point: 'bg-indigo-100 text-indigo-800',
        delivered: 'bg-green-100 text-green-800',
        picked: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800',
        refund_initiated: 'bg-yellow-100 text-yellow-800',
        refund_processing: 'bg-blue-100 text-blue-800',
        refund_completed: 'bg-green-100 text-green-800',
        refund_failed: 'bg-red-100 text-red-800'
    };

    return colors[status] || 'bg-gray-100 text-gray-800';
};

const KdpSettlementInvoiceCard = ({
    order,
    onView,
    onDownload,
    isViewing,
    isDownloading,
    canGenerate
}) => {
    const breakdown = getSettlementBreakdown(order);
    const itemCount = order.itemCount ?? (Array.isArray(order.items) ? order.items.length : 0);
    const paymentMethod = displayText(order.paymentMethod, '—').toUpperCase();
    const orderType = formatStatusLabel(order.orderType);
    const customerName = displayText(order.userId?.fullName, '—');
    const orderDate = formatDateShort(order.createdAt) || '—';
    const actionsDisabled = !canGenerate || isViewing || isDownloading;

    return (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-md overflow-hidden">
            <div className="px-4 sm:px-5 py-3 bg-[#12355B] text-white">
                <h3 className="text-sm font-bold tracking-wide">KDP Settlement Invoice</h3>
            </div>
            <div className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-base font-bold text-slate-900">
                            Order Id: {formatDisplayOrderId(order.orderId)}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">Customer: {customerName}</p>
                        <p className="text-sm text-slate-500">Order Date: {orderDate}</p>
                    </div>
                    <div className="sm:text-right text-sm text-slate-500 space-y-1">
                        <p className="flex items-center gap-1.5 sm:justify-end">
                            <ShoppingCart size={14} />
                            {itemCount} {itemCount === 1 ? 'item' : 'items'}
                        </p>
                        <p className="flex items-center gap-1.5 sm:justify-end">
                            <CreditCard size={14} />
                            <span className="uppercase tracking-wide">{paymentMethod}</span>
                        </p>
                        <p>Order Type: {orderType}</p>
                    </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100">
                    <h4 className="text-sm font-bold text-[#12355B] mb-3">Bill Details</h4>
                    <div className="rounded-xl bg-slate-50 px-4 py-3 space-y-2.5">
                        <div className="flex justify-between gap-4 text-sm">
                            <span className="text-slate-700">Total Order Value</span>
                            <span className="font-medium text-slate-900 tabular-nums">{formatMoney(breakdown.orderValue)}</span>
                        </div>
                        <div className="flex justify-between gap-4 text-sm">
                            <span className="text-slate-700">Payment Gateway Charges</span>
                            <span className="font-medium text-red-500 tabular-nums">-{formatMoney(breakdown.gateway)}</span>
                        </div>
                        <div className="flex justify-between gap-4 text-sm">
                            <span className="text-slate-700">Commission</span>
                            <span className="font-medium text-red-500 tabular-nums">-{formatMoney(breakdown.commission)}</span>
                        </div>
                        <div className="flex justify-between gap-4 text-sm pt-1">
                            <span className="font-bold text-slate-800">Total Earnings</span>
                            <span className="font-bold text-green-600 tabular-nums">{formatMoney(breakdown.earnings)}</span>
                        </div>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                    <button
                        type="button"
                        onClick={onView}
                        disabled={actionsDisabled}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#12355B] text-[#12355B] bg-white text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isViewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye size={16} />}
                        View
                    </button>
                    <button
                        type="button"
                        onClick={onDownload}
                        disabled={actionsDisabled}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#12355B] text-white text-sm font-semibold hover:bg-[#0e2c4a] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={16} />}
                        Download
                    </button>
                </div>
                {!canGenerate && (
                    <p className="text-xs text-slate-500 mt-2">
                        View and download are available after the order is delivered or picked up.
                    </p>
                )}
            </div>
        </div>
    );
};

const OrderManagementSystem = () => {
    const permissions = usePermissions(['Order Details', 'finance/orderinvoices']);

    const [orders, setOrders] = useState([]);
    const [searchCustomerName, setSearchCustomerName] = useState('');
    const [searchShopName, setSearchShopName] = useState('');
    const [searchOrderId, setSearchOrderId] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [minPrice, setMinPrice] = useState(''); // New state for min price
    const [maxPrice, setMaxPrice] = useState(''); // New state for max price
    const [orderType, setOrderType] = useState('all'); // New state for order type
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalOrdersCount, setTotalOrdersCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [detailTab, setDetailTab] = useState('overview');
    const [error, setError] = useState(null);
    const [isGlobalDownloadingPdf, setIsGlobalDownloadingPdf] = useState(false);
    const [isPreviewingHtml, setIsPreviewingHtml] = useState(false);
    const [htmlInvoiceContent, setHtmlInvoiceContent] = useState('');
    const [invoicePreviewTitle, setInvoicePreviewTitle] = useState('Invoice Preview');
    const [authToken] = useState(localStorage.getItem('token'));
    const [successMessage, setSuccessMessage] = useState(null);
    const [showCancelRefundModal, setShowCancelRefundModal] = useState(false);
    const [cancelRefundReason, setCancelRefundReason] = useState('');
    const [isCancellingRefund, setIsCancellingRefund] = useState(false);
    const [isRetryingRefund, setIsRetryingRefund] = useState(false);
    const [showAssignShopMemberModal, setShowAssignShopMemberModal] = useState(false);
    const [showReassignShopMemberModal, setShowReassignShopMemberModal] = useState(false);
    const [showAssignDeliveryBoyModal, setShowAssignDeliveryBoyModal] = useState(false);
    const [deliveryBoyModalMode, setDeliveryBoyModalMode] = useState('assign');
    const [teamMembersList, setTeamMembersList] = useState([]);
    const [deliveryBoysList, setDeliveryBoysList] = useState([]);
    const [selectedTeamMemberId, setSelectedTeamMemberId] = useState('');
    const [selectedDeliveryBoyUserId, setSelectedDeliveryBoyUserId] = useState('');
    const [assignmentNotes, setAssignmentNotes] = useState('');
    const [loadingAssignmentOptions, setLoadingAssignmentOptions] = useState(false);
    const [submittingAssignment, setSubmittingAssignment] = useState(false);
    const [openActionMenuId, setOpenActionMenuId] = useState(null);

    const isInitialMount = useRef(true);
    const fetchTimeoutRef = useRef(null);
    const actionMenuRef = useRef(null);

    const fetchOrders = useCallback(async (pageToFetch = currentPage) => {
        setLoading(true);
        setError(null);
        let endpoint = '/admin-panel/orders-and-invoices/orders';
        const params = {};

        const hasSearch = searchCustomerName.trim() || searchShopName.trim() || searchOrderId.trim();
        const hasFilter = filterStatus !== 'all' || dateRange.start || dateRange.end || minPrice || maxPrice || orderType !== 'all';

        // Use /search for customer name, shop name and orderId (backend expects customerName, shopName & orderId)
        if (hasSearch) {
            endpoint = '/admin-panel/orders-and-invoices/orders/search';
            if (searchCustomerName.trim()) params.customerName = searchCustomerName.trim().toLowerCase();
            if (searchShopName.trim()) params.shopName = searchShopName.trim().toLowerCase();
            if (searchOrderId.trim()) params.orderId = searchOrderId.trim();
            if (filterStatus !== 'all') params.orderStatus = filterStatus.toUpperCase();
            if (dateRange.start) params.startDate = dateRange.start;
            if (dateRange.end) params.endDate = dateRange.end;
            if (minPrice) params.minPrice = minPrice;
            if (maxPrice) params.maxPrice = maxPrice;
            if (orderType !== 'all') params.orderType = orderType;
        } else if (hasFilter) {
            endpoint = '/admin-panel/orders-and-invoices/orders/filter';
            if (filterStatus !== 'all') {
                params.orderStatus = filterStatus.toUpperCase();
            }
            if (dateRange.start) {
                params.startDate = dateRange.start;
            }
            if (dateRange.end) {
                params.endDate = dateRange.end;
            }
            if (minPrice) {
                params.minPrice = minPrice;
            }
            if (maxPrice) {
                params.maxPrice = maxPrice;
            }
            if (orderType !== 'all') {
                params.orderType = orderType;
            }
        }

        params.page = pageToFetch;
        params.limit = ORDERS_PER_PAGE;

        try {
            console.log('API Call:', endpoint, params); // Debug log
            const response = await apiWithAuth.get(endpoint, { params });
            const data = response.data;
            setOrders(data.orders ?? []);
            setTotalPages(data.totalPages ?? 1);
            setCurrentPage(data.currentPage ?? 1);
            setTotalOrdersCount(data.totalOrders ?? 0);

        } catch (error) {
            console.error("Failed to fetch orders:", error);
            setError(error.message);
            setOrders([]);
            setTotalPages(1);
            setTotalOrdersCount(0);
        } finally {
            setLoading(false);
        }
    }, [searchCustomerName, searchShopName, searchOrderId, filterStatus, dateRange.start, dateRange.end, minPrice, maxPrice, orderType]);

    // Single effect to handle all data fetching
    useEffect(() => {
        if (!authToken) return;

        // Clear any existing timeout
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        // Initial mount - fetch immediately
        if (isInitialMount.current) {
            fetchOrders(1);
            isInitialMount.current = false;
            return;
        }

        // Filter changes - debounce and reset to page 1
        if (currentPage === 1) {
            fetchTimeoutRef.current = setTimeout(() => {
                fetchOrders(1);
            }, 300);
        } else {
            // Reset to page 1 when filters change
            setCurrentPage(1);
        }

        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, [authToken, searchCustomerName, searchShopName, searchOrderId, filterStatus, dateRange.start, dateRange.end, minPrice, maxPrice, orderType, fetchOrders]);

    // Separate effect for pagination changes only
    useEffect(() => {
        if (!authToken || isInitialMount.current || currentPage === 1) return;

        fetchOrders(currentPage);
    }, [currentPage, authToken, fetchOrders]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!openActionMenuId) return;

        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setOpenActionMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openActionMenuId]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleViewDetails = async (order) => {
        if (!order?.orderId) return;

        setSelectedOrder(order);
        setDetailTab('overview');
        setViewMode('detail');
        setLoadingOrderDetails(true);
        setError(null);

        try {
            const response = await orderAdminApi.searchOrder(order.orderId);
            const fullOrder = extractOrderFromSearchResponse(response.data);
            if (fullOrder?.orderId) {
                setSelectedOrder(fullOrder);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load order details');
        } finally {
            setLoadingOrderDetails(false);
        }
    };

    const handleBackToList = () => {
        setViewMode('list');
        setSelectedOrder(null);
        setOpenActionMenuId(null);
        setHtmlInvoiceContent('');
        setIsPreviewingHtml(false);
        setInvoicePreviewTitle('Invoice Preview');
        setError(null);
        setSuccessMessage(null);
        setShowAssignShopMemberModal(false);
        setShowReassignShopMemberModal(false);
        setShowAssignDeliveryBoyModal(false);
        setDeliveryBoyModalMode('assign');
        setTeamMembersList([]);
        setDeliveryBoysList([]);
        setSelectedTeamMemberId('');
        setSelectedDeliveryBoyUserId('');
        setAssignmentNotes('');
    };

    const refreshSelectedOrder = async (orderId) => {
        const response = await orderAdminApi.searchOrder(orderId);
        const updated = extractOrderFromSearchResponse(response.data);
        if (updated?.orderId) {
            setSelectedOrder(updated);
        }
    };

    const loadTeamMembersForShop = async (shopId) => {
        const response = await orderAdminApi.getTeamMembers(shopId);
        return response.data?.data || [];
    };

    const loadDeliveryBoysForOrder = async (order) => {
        if (order.deliveryFrom === 'KD') {
            const response = await orderAdminApi.getKdDeliveryBoys(order.orderId);
            return extractDeliveryBoyEntries(response.data)
                .filter(Boolean)
                .map((boy) => ({
                    userId: resolveRefId(boy),
                    fullName: boy.fullName || boy.displayName,
                    phone: boy.phone
                }))
                .filter((boy) => boy.userId);
        }

        const shopId = getOrderShopId(order);
        if (!shopId) {
            throw new Error('Shop information not available for this order');
        }

        const response = await orderAdminApi.getShopDeliveryBoys(shopId);
        return extractDeliveryBoyEntries(response.data)
            .filter((entry) => entry?.userId)
            .map((entry) => ({
                userId: resolveRefId(entry.userId),
                fullName: entry.userId?.fullName || entry.userId?.roleId?.fullName || entry.fullName,
                phone: entry.userId?.phone || entry.userId?.roleId?.phone || entry.phone
            }))
            .filter((boy) => boy.userId);
    };

    const openAssignShopMemberModal = async (order = selectedOrder) => {
        const shopId = getOrderShopId(order);
        if (!shopId) {
            setError('Shop information not available for this order');
            return;
        }
        setSelectedOrder(order);
        setLoadingAssignmentOptions(true);
        setError(null);
        setSelectedTeamMemberId('');
        setAssignmentNotes('');
        try {
            const members = await loadTeamMembersForShop(shopId);
            setTeamMembersList(members);
            setShowAssignShopMemberModal(true);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load team members');
        } finally {
            setLoadingAssignmentOptions(false);
        }
    };

    const openReassignShopMemberModal = async (order = selectedOrder) => {
        const shopId = getOrderShopId(order);
        if (!shopId) {
            setError('Shop information not available for this order');
            return;
        }
        setSelectedOrder(order);
        setLoadingAssignmentOptions(true);
        setError(null);
        setSelectedTeamMemberId('');
        setAssignmentNotes('');
        try {
            const members = await loadTeamMembersForShop(shopId);
            const currentUserId = getShopMemberRefId(
                order.shopMembers?.find((member) => getShopMemberRefId(member))
            );
            setTeamMembersList(
                members.filter((member) => resolveRefId(member.userId) !== currentUserId)
            );
            setShowReassignShopMemberModal(true);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load team members');
        } finally {
            setLoadingAssignmentOptions(false);
        }
    };

    const openAssignDeliveryBoyModal = async (order = selectedOrder) => {
        if (!order) return;
        setSelectedOrder(order);
        setLoadingAssignmentOptions(true);
        setError(null);
        setSelectedDeliveryBoyUserId('');
        setDeliveryBoyModalMode('assign');
        try {
            const boys = await loadDeliveryBoysForOrder(order);
            setDeliveryBoysList(boys);
            setShowAssignDeliveryBoyModal(true);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load delivery partners');
        } finally {
            setLoadingAssignmentOptions(false);
        }
    };

    const openReassignDeliveryBoyModal = async (order = selectedOrder) => {
        if (!order) return;
        setSelectedOrder(order);
        setLoadingAssignmentOptions(true);
        setError(null);
        setSelectedDeliveryBoyUserId('');
        setDeliveryBoyModalMode('reassign');
        try {
            const boys = await loadDeliveryBoysForOrder(order);
            const currentId = getDeliveryPartnerId(order);
            setDeliveryBoysList(
                boys.filter((boy) => String(boy.userId) !== String(currentId))
            );
            setShowAssignDeliveryBoyModal(true);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to load delivery partners');
        } finally {
            setLoadingAssignmentOptions(false);
        }
    };

    const handleAssignShopMember = async () => {
        if (!selectedOrder || !selectedTeamMemberId) return;
        setSubmittingAssignment(true);
        setError(null);
        try {
            const shopId = getOrderShopId(selectedOrder);
            if (!shopId) {
                throw new Error('Shop information not available for this order');
            }
            const response = await orderAdminApi.assignShopMember(selectedOrder.orderId, {
                shopId,
                teamMemberId: selectedTeamMemberId,
                notes: assignmentNotes.trim() || undefined
            });
            setSuccessMessage(response.data?.message || 'Shop member assigned successfully');
            setShowAssignShopMemberModal(false);
            setAssignmentNotes('');
            await refreshSelectedOrder(selectedOrder.orderId);
            fetchOrders(currentPage);
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to assign shop member');
        } finally {
            setSubmittingAssignment(false);
        }
    };

    const handleReassignShopMember = async () => {
        if (!selectedOrder || !selectedTeamMemberId) return;
        setSubmittingAssignment(true);
        setError(null);
        try {
            const shopId = getOrderShopId(selectedOrder);
            if (!shopId) {
                throw new Error('Shop information not available for this order');
            }
            const response = await orderAdminApi.reassignShopMember(selectedOrder.orderId, {
                shopId,
                newTeamMemberId: selectedTeamMemberId,
                notes: assignmentNotes.trim() || undefined
            });
            setSuccessMessage(response.data?.message || 'Shop member reassigned successfully');
            setShowReassignShopMemberModal(false);
            setAssignmentNotes('');
            await refreshSelectedOrder(selectedOrder.orderId);
            fetchOrders(currentPage);
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to reassign shop member');
        } finally {
            setSubmittingAssignment(false);
        }
    };

    const handleAssignDeliveryBoy = async () => {
        if (!selectedOrder || !selectedDeliveryBoyUserId) return;
        setSubmittingAssignment(true);
        setError(null);
        try {
            const response = await orderAdminApi.assignDeliveryBoy(
                selectedOrder.orderId,
                selectedDeliveryBoyUserId
            );
            const isReassign = deliveryBoyModalMode === 'reassign';
            setSuccessMessage(
                response.data?.message
                || (isReassign ? 'Delivery partner reassigned successfully' : 'Delivery partner assigned successfully')
            );
            setShowAssignDeliveryBoyModal(false);
            setDeliveryBoyModalMode('assign');
            await refreshSelectedOrder(selectedOrder.orderId);
            fetchOrders(currentPage);
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to assign delivery partner');
        } finally {
            setSubmittingAssignment(false);
        }
    };

    const handleClosePreview = () => {
        setIsPreviewingHtml(false);
        setHtmlInvoiceContent('');
        setInvoicePreviewTitle('Invoice Preview');
    };

    const handleCancelAndRefund = async () => {
        if (!selectedOrder) return;

        setIsCancellingRefund(true);
        setError(null);

        try {
            const response = await apiWithAuth.post(
                `/admin-panel/orders-and-invoices/orders/${selectedOrder.orderId}/cancel-and-refund`,
                { reason: cancelRefundReason.trim() || 'Cancelled by admin' }
            );

            const { data, message } = response.data;
            setSelectedOrder((prev) => ({
                ...prev,
                status: data.status,
                paymentStatus: prev.paymentStatus,
                refundStatus: data.refundStatus
            }));
            setSuccessMessage(message || 'Order cancelled and refund initiated successfully.');
            setShowCancelRefundModal(false);
            setCancelRefundReason('');
            setTimeout(() => setSuccessMessage(null), 5000);
            fetchOrders(currentPage);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel and refund order';
            setError(errorMessage);
            setTimeout(() => setError(null), 5000);
        } finally {
            setIsCancellingRefund(false);
        }
    };

    const handleRetryRefund = async (order = selectedOrder) => {
        if (!order) return;

        setSelectedOrder(order);
        setIsRetryingRefund(true);
        setError(null);

        try {
            const response = await apiWithAuth.post(
                `/admin-panel/orders-and-invoices/orders/${order.orderId}/retry-refund`
            );

            const { data, message } = response.data;
            setSelectedOrder((prev) => ({
                ...prev,
                status: data.status,
                refundStatus: data.refundStatus,
                refundDetails: data.refundDetails
            }));
            setSuccessMessage(message || 'Refund retry initiated successfully.');
            setTimeout(() => setSuccessMessage(null), 5000);
            fetchOrders(currentPage);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to retry refund';
            setError(errorMessage);
            if (err.response?.data?.data) {
                setSelectedOrder((prev) => ({
                    ...prev,
                    status: err.response.data.data.status,
                    refundStatus: err.response.data.data.refundStatus,
                    refundDetails: err.response.data.data.refundDetails
                }));
            }
            setTimeout(() => setError(null), 8000);
        } finally {
            setIsRetryingRefund(false);
        }
    };


    const generateInvoice = async (type, format, order = selectedOrder) => {
        if (!order) return;

        setSelectedOrder(order);

        if (!['delivered', 'picked'].includes(order.status)) {
            setError('Invoices are only available for delivered or picked up orders');
            setTimeout(() => setError(null), 5000);
            return;
        }
        // Validate: Don't generate delivery invoice for pickup orders
        if (type === 'delivery-fee' && order.orderType === 'pickup') {
            setError('Delivery invoice cannot be generated for pickup orders');
            setTimeout(() => setError(null), 5000);
            return;
        }

        // Validate: Don't generate delivery invoice if no delivery fee
        if (type === 'delivery-fee' && (!order.deliveryFee || order.deliveryFee === 0)) {
            setError('No delivery fee charged for this order');
            setTimeout(() => setError(null), 5000);
            return;
        }

        // Validate: Don't generate platform invoice if no platform fee
        if (type === 'platform-fee' && (!order.platformFee || order.platformFee === 0)) {
            setError('No platform fee charged for this order');
            setTimeout(() => setError(null), 5000);
            return;
        }

        if (format === 'pdf') {
            setIsGlobalDownloadingPdf(true);
        } else if (format === 'html') {
            setIsPreviewingHtml(true);
        }

        try {
            const response = await apiWithAuth.get(`/admin-panel/orders-and-invoices/orders/${order.orderId}/invoice`, {
                params: { type, format },
                responseType: format === 'pdf' ? 'blob' : 'json'
            });

            if (format === 'pdf') {
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${formatDisplayOrderId(order.orderId)}_${type}_invoice.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                setSuccessMessage('Invoice downloaded successfully!');
                setTimeout(() => setSuccessMessage(null), 3000);
            } else if (format === 'html') {
                const html = extractInvoiceHtml(response.data);
                if (!html) {
                    throw new Error('Invoice preview was empty');
                }
                setInvoicePreviewTitle('Invoice Preview');
                setHtmlInvoiceContent(html);
            }

        } catch (error) {
            console.error("Error generating invoice:", error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to generate invoice';
            setError(errorMessage);
            setTimeout(() => setError(null), 5000);

            // Show user-friendly error notification
            if (format === 'html') {
                setIsPreviewingHtml(false);
            }
        } finally {
            if (format === 'pdf') {
                setIsGlobalDownloadingPdf(false);
            }
        }
    };

    const viewShopSettlementInvoice = async (order = selectedOrder) => {
        if (!order?.orderId) return;
        if (!canDownloadInvoice(order)) {
            setError('KDP settlement invoices are only available for delivered or picked up orders');
            setTimeout(() => setError(null), 5000);
            return;
        }

        setSelectedOrder(order);
        setHtmlInvoiceContent('');
        setInvoicePreviewTitle('Settlement Invoice');
        setIsPreviewingHtml(true);
        try {
            const response = await apiWithAuth.get('/kdp-invoices/shop-invoice', {
                params: { orderId: order.orderId, onlyHtml: true }
            });
            const html = extractInvoiceHtml(response.data);
            if (!html) {
                throw new Error('Invoice preview was empty');
            }
            setHtmlInvoiceContent(html);
        } catch (error) {
            console.error('Error loading KDP settlement invoice:', error);
            setIsPreviewingHtml(false);
            setHtmlInvoiceContent('');
            const errorMessage = error.response?.data?.message || error.message || 'Failed to load KDP settlement invoice';
            setError(errorMessage);
            setTimeout(() => setError(null), 5000);
        }
    };

    const downloadShopSettlementInvoice = async (order = selectedOrder) => {
        if (!order?.orderId) return;
        if (!canDownloadInvoice(order)) {
            setError('KDP settlement invoices are only available for delivered or picked up orders');
            setTimeout(() => setError(null), 5000);
            return;
        }

        setSelectedOrder(order);
        setIsGlobalDownloadingPdf(true);
        try {
            const response = await apiWithAuth.get('/kdp-invoices/shop-invoice', {
                params: { orderId: order.orderId, onlyHtml: false },
                responseType: 'blob'
            });
            const contentType = response.headers['content-type'] || '';
            const isPdf = contentType.includes('pdf');
            const blob = new Blob([response.data], {
                type: isPdf ? 'application/pdf' : (contentType || 'application/octet-stream')
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${formatDisplayOrderId(order.orderId)}_kdp_settlement_invoice.${isPdf ? 'pdf' : 'html'}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setSuccessMessage('KDP settlement invoice downloaded successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            console.error('Error downloading KDP settlement invoice:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to download KDP settlement invoice';
            setError(errorMessage);
            setTimeout(() => setError(null), 5000);
        } finally {
            setIsGlobalDownloadingPdf(false);
        }
    };


    // Show loading if no token is available yet
    if (authToken === null) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // Show error if no token found
    if (!authToken) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        <p className="font-bold">Authentication Required</p>
                        <p>Please login to access this page.</p>
                    </div>
                </div>
            </div>
        );
    }

    const visibleOrderItems = getVisibleOrderItems(selectedOrder?.items);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-6 sm:mb-8 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Order Management</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            {viewMode === 'list' ? 'View and manage all orders' : 'Order details'}
                        </p>
                    </div>
                    {viewMode === 'detail' && (
                        <button
                            onClick={handleBackToList}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-200 shadow-sm hover:bg-slate-50"
                        >
                            <ArrowLeft size={16} />
                            Back to Orders
                        </button>
                    )}
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 flex items-center justify-between" role="alert">
                        <div>
                            <strong className="font-bold">Error!</strong>
                            <span className="block sm:inline"> {error}</span>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-700 hover:text-red-900"
                        >
                            <X size={20} />
                        </button>
                    </div>
                )}

                {successMessage && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 flex items-center justify-between" role="alert">
                        <div className="flex items-center">
                            <Check className="mr-2" size={20} />
                            <span className="font-medium">{successMessage}</span>
                        </div>
                        <button
                            onClick={() => setSuccessMessage(null)}
                            className="text-green-700 hover:text-green-900"
                        >
                            <X size={20} />
                        </button>
                    </div>
                )}

                {viewMode === 'list' ? (
                    <>
                        {/* Total Orders Count */}
                        <div className="mb-4 text-lg font-semibold text-gray-800">
                            Total Orders: {loading ? <Loader2 className="inline-block animate-spin mr-1 h-5 w-5 text-blue-500" /> : totalOrdersCount}
                        </div>

                        {/* Filters */}
                        <div className="bg-white shadow rounded-lg mb-6 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

                                {/* Search by customer name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <User className="h-4 w-4 text-gray-500" />
                                        Customer name
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Customer name..."
                                        value={searchCustomerName}
                                        onChange={(e) => setSearchCustomerName(e.target.value)}
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                                    />
                                </div>

                                {/* Search by shop name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <Store className="h-4 w-4 text-gray-500" />
                                        Shop name
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Shop name..."
                                        value={searchShopName}
                                        onChange={(e) => setSearchShopName(e.target.value)}
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                                    />
                                </div>

                                {/* Search by order ID */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <Receipt className="h-4 w-4 text-gray-500" />
                                        Order ID
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Order ID..."
                                        value={searchOrderId}
                                        onChange={(e) => setSearchOrderId(e.target.value)}
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                                    />
                                </div>

                                {/* From Order Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <BadgeInfo className="h-4 w-4 text-gray-500" />
                                        Status
                                    </label>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    >
                                        <option value="all">All Status</option>
                                        {Object.entries(statusOptions).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* From Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <CalendarDays className="h-4 w-4 text-gray-500" />
                                        From Date
                                    </label>
                                    <input
                                        type="date"
                                        value={dateRange.start}
                                        onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                                    />
                                </div>

                                {/* To Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <CalendarDays className="h-4 w-4 text-gray-500" />
                                        To Date
                                    </label>
                                    <input
                                        type="date"
                                        value={dateRange.end}
                                        onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                                    />
                                </div>

                                {/* Min Price */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <IndianRupee className="h-4 w-4 text-gray-500" />
                                        Min Price
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Min price..."
                                        value={minPrice}
                                        onChange={(e) => setMinPrice(e.target.value)}
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                                    />
                                </div>

                                {/* Max Price */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <IndianRupee className="h-4 w-4 text-gray-500" />
                                        Max Price
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="Max price..."
                                        value={maxPrice}
                                        onChange={(e) => setMaxPrice(e.target.value)}
                                        className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3"
                                    />
                                </div>

                                {/* Order Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                        <ShoppingCart className="h-4 w-4 text-gray-500" />
                                        Order Type
                                    </label>
                                    <select
                                        value={orderType}
                                        onChange={(e) => setOrderType(e.target.value)}
                                        className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    >
                                        <option value="all">All Types</option>
                                        <option value="delivery">Delivery</option>
                                        <option value="pickup">Pickup</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Orders Table */}
                        <div className="bg-white shadow rounded-lg">
                            {loading ? (
                                <div className="p-12 text-center text-gray-500">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                    Loading orders...
                                </div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Order ID
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Customer
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Shop Name
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Date
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Items
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Total
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {orders.length > 0 ? (
                                                    orders.map((order) => (
                                                        <tr key={order._id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                #{formatDisplayOrderId(order.orderId)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                                <div className="flex flex-col">
                                                                    <span className="font-medium">
                                                                        {order.userId?.fullName || <span className="text-gray-400 italic">Not available</span>}
                                                                    </span>
                                                                    {order.userId?.phone && (
                                                                        <span className="text-gray-500 text-xs">
                                                                            {order.userId.phone}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {order.shopId?.name || <span className="text-gray-400 italic">Not available</span>}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatDate(order.createdAt) || <span className="text-gray-400 italic">-</span>}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <OrderStatusBadge status={order.status} size="sm" />
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {order.itemCount || 0}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {formatCurrency(order.grandTotal) || <span className="text-gray-400">₹0.00</span>}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                <button
                                                                    onClick={() => handleViewDetails(order)}
                                                                    className="text-blue-600 hover:text-blue-900 mr-3"
                                                                >
                                                                    <Eye className="h-5 w-5" />
                                                                </button>
                                                                <div
                                                                    className="relative inline-block text-left"
                                                                    ref={openActionMenuId === order._id ? actionMenuRef : null}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenActionMenuId(
                                                                                openActionMenuId === order._id ? null : order._id
                                                                            );
                                                                        }}
                                                                        className="text-gray-400 hover:text-gray-600"
                                                                        aria-label="More actions"
                                                                        aria-expanded={openActionMenuId === order._id}
                                                                    >
                                                                        <MoreVertical className="h-5 w-5" />
                                                                    </button>
                                                                    {openActionMenuId === order._id && (
                                                                        <div className="absolute right-0 z-30 mt-1 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                                                                            <div className="py-1">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setOpenActionMenuId(null);
                                                                                        handleViewDetails(order);
                                                                                    }}
                                                                                    className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                                >
                                                                                    <Eye className="mr-2 h-4 w-4" />
                                                                                    View Details
                                                                                </button>
                                                                                {canDownloadInvoice(order) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setOpenActionMenuId(null);
                                                                                            generateInvoice('main', 'pdf', order);
                                                                                        }}
                                                                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                                    >
                                                                                        <Download className="mr-2 h-4 w-4" />
                                                                                        Download Invoice
                                                                                    </button>
                                                                                )}
                                                                                {canAssignDeliveryBoy(order) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setOpenActionMenuId(null);
                                                                                            openAssignDeliveryBoyModal(order);
                                                                                        }}
                                                                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                                    >
                                                                                        <UserPlus className="mr-2 h-4 w-4" />
                                                                                        Assign Delivery Partner
                                                                                    </button>
                                                                                )}
                                                                                {canReassignDeliveryBoy(order) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setOpenActionMenuId(null);
                                                                                            openReassignDeliveryBoyModal(order);
                                                                                        }}
                                                                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                                    >
                                                                                        <UserCog className="mr-2 h-4 w-4" />
                                                                                        Reassign Delivery Partner
                                                                                    </button>
                                                                                )}
                                                                                {canAssignShopMember(order) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setOpenActionMenuId(null);
                                                                                            openAssignShopMemberModal(order);
                                                                                        }}
                                                                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                                    >
                                                                                        <UserPlus className="mr-2 h-4 w-4" />
                                                                                        Assign Shop Member
                                                                                    </button>
                                                                                )}
                                                                                {canReassignShopMember(order) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setOpenActionMenuId(null);
                                                                                            openReassignShopMemberModal(order);
                                                                                        }}
                                                                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                                    >
                                                                                        <UserCog className="mr-2 h-4 w-4" />
                                                                                        Reassign Shop Member
                                                                                    </button>
                                                                                )}
                                                                                {canRetryRefundOrder(order) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setOpenActionMenuId(null);
                                                                                            handleRetryRefund(order);
                                                                                        }}
                                                                                        className="flex w-full items-center px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
                                                                                    >
                                                                                        <RefreshCw className="mr-2 h-4 w-4" />
                                                                                        Retry Refund
                                                                                    </button>
                                                                                )}
                                                                                {canCancelAndRefundOrder(order) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            setOpenActionMenuId(null);
                                                                                            setSelectedOrder(order);
                                                                                            setShowCancelRefundModal(true);
                                                                                        }}
                                                                                        className="flex w-full items-center px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                                                                    >
                                                                                        <X className="mr-2 h-4 w-4" />
                                                                                        Cancel & Refund
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="8" className="p-12 text-center">
                                                            <Package className="mx-auto h-12 w-12 text-gray-400" />
                                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
                                                            <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria</p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav
                                            className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
                                            aria-label="Pagination"
                                        >
                                            <div className="flex-1 flex justify-between items-center sm:justify-end">
                                                {/* Previous Button */}
                                                <button
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Previous
                                                </button>
                                                {/* Page Info (visible on smaller screens) */}
                                                <div className="sm:hidden text-sm text-gray-700 mx-3">
                                                    Page <span className="font-medium">{currentPage}</span> of{' '}
                                                    <span className="font-medium">{totalPages}</span>
                                                </div>
                                                {/* Next Button */}
                                                <button
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                            {/* Page Info (visible on larger screens) */}
                                            <div className="hidden sm:flex sm:items-center">
                                                <p className="text-sm text-gray-700">
                                                    Showing <span className="font-medium">{(currentPage - 1) * ORDERS_PER_PAGE + 1}</span> to{' '}
                                                    <span className="font-medium">{Math.min(currentPage * ORDERS_PER_PAGE, totalOrdersCount)}</span> of{' '}
                                                    <span className="font-medium">{totalOrdersCount}</span> results
                                                </p>
                                            </div>
                                        </nav>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    /* Order Detail View — CS-focused layout */
                    selectedOrder && (
                        <div className="bg-white shadow rounded-xl max-w-full overflow-x-hidden relative">
                            {loadingOrderDetails && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                                </div>
                            )}
                            {/* Order Header */}
                            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">Order #{formatDisplayOrderId(selectedOrder.orderId)}</h1>

                                    <div className="flex items-center flex-wrap gap-2 mt-2">
                                        {/* Status badge */}
                                        <OrderStatusBadge status={selectedOrder.status} size="md" />

                                        {/* Order type badge */}
                                        {selectedOrder.orderType && (
                                            <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 capitalize flex items-center">
                                                {selectedOrder.orderType === 'delivery' ? <Truck className="mr-1" size={14} /> : <Package className="mr-1" size={14} />}
                                                {selectedOrder.orderType}
                                            </span>
                                        )}

                                        {/* Order date */}
                                        {formatDate(selectedOrder.createdAt) && (
                                            <span className="text-sm text-gray-500 flex items-center">
                                                <Calendar className="inline mr-1" size={14} />
                                                {formatDate(selectedOrder.createdAt)}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                        <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shrink-0 min-w-[180px] shadow-sm">
                                            <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Order total</p>
                                            <p className="text-3xl font-bold mt-1 text-slate-900 tabular-nums">
                                                {formatMoney(selectedOrder.grandTotal ?? selectedOrder.updatedGrandTotal)}
                                            </p>
                                            <p className="text-sm text-slate-500 mt-1">
                                                {selectedOrder.itemCount ?? selectedOrder.items?.length ?? 0} item(s)
                                            </p>
                                        </div>
                            </div>

                                {/* Quick contact strip */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-100 border-b border-slate-100">
                                    {[
                                        {
                                            label: 'Customer',
                                            name: selectedOrder.userId?.fullName,
                                            phone: selectedOrder.userId?.phone,
                                            sub: selectedOrder.userId?.email
                                        },
                                        {
                                            label: 'Shop',
                                            name: selectedOrder.shopId?.name,
                                            phone: selectedOrder.shopId?.phone,
                                            sub: selectedOrder.shopId?.gstNumber ? `GSTIN ${selectedOrder.shopId.gstNumber}` : null
                                        },
                                        {
                                            label: 'Shop staff',
                                            name: selectedOrder.shopMembers?.[0]?.fullName,
                                            phone: selectedOrder.shopMembers?.[0]?.phone,
                                            sub: selectedOrder.shopMembers?.[0]?.status
                                        },
                                        {
                                            label: 'Delivery partner',
                                            name: selectedOrder.deliveryBoy?.fullName,
                                            phone: selectedOrder.deliveryBoy?.phone,
                                            sub: selectedOrder.deliveryBoy?.deliveryStatus || (selectedOrder.orderType === 'pickup' ? 'Pickup order' : null)
                                        }
                                    ].map((card) => (
                                        <div key={card.label} className="px-4 py-4 bg-white hover:bg-slate-50/80 transition-colors">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{card.label}</p>
                                            <p className="text-[15px] font-semibold text-slate-900 mt-1.5 truncate leading-snug">
                                                {displayText(card.name, 'Not assigned')}
                                            </p>
                                            <div className="mt-2 flex flex-wrap items-center gap-1.5 min-h-[22px]">
                                                {card.phone ? (
                                                    <>
                                                        <ContactLink phone={card.phone} />
                                                        <CopyButton text={card.phone} label="Copy" />
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-slate-400">No phone</span>
                                                )}
                                            </div>
                                            {card.sub ? (
                                                <p className="text-xs text-slate-500 mt-1.5 truncate capitalize">{card.sub}</p>
                                            ) : (
                                                <p className="text-xs text-slate-300 mt-1.5">—</p>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {selectedOrder.specialInstructions && (
                                    <div className="px-4 sm:px-6 py-3 bg-amber-50 border-b border-amber-100 flex gap-2.5 items-start">
                                        <MessageSquare className="text-amber-600 shrink-0 mt-0.5" size={16} />
                                        <div>
                                            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700">Special instructions</p>
                                            <p className="text-sm text-amber-950 mt-0.5 whitespace-pre-wrap leading-relaxed">{selectedOrder.specialInstructions}</p>
                                        </div>
                                    </div>
                                )}

                                {/* Tabs */}
                                <div className="px-2 sm:px-4 flex gap-1 overflow-x-auto bg-white">
                                    {[
                                        { id: 'overview', label: 'Overview', icon: ShoppingCart },
                                        { id: 'fulfillment', label: 'Fulfillment', icon: Truck },
                                        { id: 'billing', label: 'Payment & Billing', icon: CreditCard }
                                    ].map((tab) => {
                                        const Icon = tab.icon;
                                        const active = detailTab === tab.id;
                                        return (
                                            <button
                                                key={tab.id}
                                                type="button"
                                                onClick={() => setDetailTab(tab.id)}
                                                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                                                    active
                                                        ? 'border-sky-600 text-sky-800'
                                                        : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-200'
                                                }`}
                                            >
                                                <Icon size={15} />
                                                {tab.label}
                                            </button>
                                        );
                                    })}
                                </div>

                            {/* OVERVIEW TAB */}
                            {detailTab === 'overview' && (
                            <>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="space-y-4 min-w-0">
                                    <DetailSection title="Customer" icon={User}>
                                        <dl className="space-y-0">
                                            <MetaRow label="Name" value={displayText(selectedOrder.userId?.fullName)} />
                                            <MetaRow label="Phone" value={<span className="inline-flex items-center gap-2"><ContactLink phone={selectedOrder.userId?.phone} /><CopyButton text={selectedOrder.userId?.phone} /></span>} />
                                            <MetaRow label="Email" value={displayText(selectedOrder.userId?.email)} />
                                        </dl>
                                        <div className="mt-3 pt-3 border-t border-slate-100">
                                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Address</p>
                                            <AddressFields address={selectedOrder.customerAddress} />
                                        </div>
                                    </DetailSection>

                                    <DetailSection title="Shop" icon={Store}>
                                        {selectedOrder.shopId ? (
                                            <>
                                                <dl className="space-y-0">
                                                    <MetaRow label="Name" value={displayText(selectedOrder.shopId.name)} />
                                                    <MetaRow label="Phone" value={<span className="inline-flex items-center gap-2"><ContactLink phone={selectedOrder.shopId.phone} /><CopyButton text={selectedOrder.shopId.phone} /></span>} />
                                                    <MetaRow label="GSTIN" value={displayText(selectedOrder.shopId.gstNumber)} />
                                                    <MetaRow label="Seller Type" value={displayText(selectedOrder.shopId.sellerType)} />
                                                </dl>
                                                <div className="mt-3 pt-3 border-t border-slate-100">
                                                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Shop address</p>
                                                    <AddressFields address={selectedOrder.shopId.address} emptyLabel="Address not available" />
                                                </div>
                                            </>
                                        ) : (
                                            <EmptyHint>Shop information not available</EmptyHint>
                                        )}
                                    </DetailSection>
                                </div>

                                {/* At-a-glance pricing for support */}
                                <div className="min-w-0">
                                    <DetailSection title="Payment summary" icon={IndianRupee}
                                        badge={<StatusPill label={formatStatusLabel(selectedOrder.paymentStatus)} tone={paymentTone(selectedOrder.paymentStatus)} />}
                                    >
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-sm"><span className="text-slate-500">Items</span><span className="font-medium">{formatMoney(selectedOrder.updatedItemTotal ?? selectedOrder.itemTotal)}</span></div>
                                            <div className="flex justify-between text-sm"><span className="text-slate-500">Discount</span><span className="font-medium text-emerald-600">{selectedOrder.discountAmount > 0 ? `- ${formatMoney(selectedOrder.discountAmount)}` : formatMoney(0)}</span></div>
                                            <div className="flex justify-between text-sm"><span className="text-slate-500">Delivery</span><span className="font-medium">{formatMoney(selectedOrder.deliveryFee)}</span></div>
                                            <div className="flex justify-between text-sm"><span className="text-slate-500">Platform fee</span><span className="font-medium">{formatMoney(selectedOrder.platformFee)}</span></div>
                                            <div className="flex justify-between text-sm"><span className="text-slate-500">GST</span><span className="font-medium">{formatMoney(selectedOrder.gstAmount)}</span></div>
                                            <div className="flex justify-between text-base border-t border-slate-100 pt-2 mt-1">
                                                <span className="font-bold text-slate-800">Total</span>
                                                <span className="font-bold text-slate-900 tabular-nums">{formatMoney(selectedOrder.grandTotal ?? selectedOrder.updatedGrandTotal)}</span>
                                            </div>
                                            {selectedOrder.paymentId && (
                                                <div className="pt-2 border-t border-dashed border-slate-200 flex items-center justify-between gap-2 text-xs text-slate-500">
                                                    <span className="font-mono break-all text-slate-700">{selectedOrder.paymentId}</span>
                                                    <CopyButton text={selectedOrder.paymentId} label="Copy" />
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setDetailTab('billing')}
                                                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900"
                                            >
                                                Full billing details <ExternalLink size={13} />
                                            </button>
                                        </div>
                                    </DetailSection>
                                </div>
                            </div>
                            </>
                            )}

                            {/* FULFILLMENT TAB */}
                            {detailTab === 'fulfillment' && (
                            <>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="space-y-4 min-w-0">
                                    <DetailSection title="Delivery Address" icon={MapPin}>
                                        <AddressFields address={selectedOrder.deliveryAddress || selectedOrder.customerAddress} emptyLabel="Delivery address not available" />
                                    </DetailSection>

                                    <DetailSection title="Pickup Address" icon={MapPin}>
                                        {selectedOrder.pickupAddress ? (
                                            <AddressFields address={selectedOrder.pickupAddress} emptyLabel="Pickup address not available" />
                                        ) : selectedOrder.orderType === 'pickup' && selectedOrder.shopId ? (
                                            <div className="space-y-2">
                                                <p className="text-xs text-slate-500">Using shop location</p>
                                                <p className="font-medium text-slate-900">{displayText(selectedOrder.shopId.name)}</p>
                                                <AddressFields address={selectedOrder.shopId.address} emptyLabel="Shop address not available" />
                                            </div>
                                        ) : (
                                            <EmptyHint>No pickup address</EmptyHint>
                                        )}
                                    </DetailSection>
                                </div>

                                <div className="space-y-4 min-w-0">
                                    {/* Delivery Partner */}
                                    <DetailSection title="Delivery Partner" icon={Truck}
                                        badge={
                                            <div className="flex flex-wrap gap-2">
                                                {canAssignDeliveryBoy(selectedOrder) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openAssignDeliveryBoyModal(selectedOrder)}
                                                        disabled={loadingAssignmentOptions}
                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
                                                    >
                                                        <UserPlus className="mr-1.5" size={14} />
                                                        Assign
                                                    </button>
                                                )}
                                                {canReassignDeliveryBoy(selectedOrder) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openReassignDeliveryBoyModal(selectedOrder)}
                                                        disabled={loadingAssignmentOptions}
                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 disabled:opacity-50"
                                                    >
                                                        <UserCog className="mr-1.5" size={14} />
                                                        Reassign
                                                    </button>
                                                )}
                                            </div>
                                        }
                                    >
                                        {selectedOrder.deliveryBoy ? (
                                            <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 flex flex-col sm:flex-row items-start gap-3">
                                                {selectedOrder.deliveryBoy.profileImageUrl ? (
                                                    <img
                                                        src={selectedOrder.deliveryBoy.profileImageUrl}
                                                        alt={selectedOrder.deliveryBoy.fullName}
                                                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 rounded-full bg-sky-200 flex items-center justify-center text-sky-700">
                                                        <User size={20} />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0 space-y-1">
                                                    <p className="font-semibold text-slate-900 flex items-center gap-2">
                                                        {displayText(selectedOrder.deliveryBoy.fullName)}
                                                        {selectedOrder.deliveryBoy.isDeleted && (
                                                            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-600">Deleted</span>
                                                        )}
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <ContactLink phone={selectedOrder.deliveryBoy.phone} />
                                                        <CopyButton text={selectedOrder.deliveryBoy.phone} />
                                                    </div>
                                                    <p className="text-sm text-slate-500">{displayText(selectedOrder.deliveryBoy.email)}</p>
                                                    <p className="text-xs text-slate-400">Assigned {displayText(formatDate(selectedOrder.deliveryBoy.assignedAt))}</p>
                                                </div>
                                                <StatusPill label={formatStatusLabel(selectedOrder.deliveryBoy.deliveryStatus)} tone="info" />
                                            </div>
                                        ) : selectedOrder.orderType === 'delivery' ? (
                                            <EmptyHint>No delivery partner assigned yet</EmptyHint>
                                        ) : (
                                            <EmptyHint>Not applicable for pickup orders</EmptyHint>
                                        )}
                                    </DetailSection>

                                    <DetailSection title="Shop Staff" icon={Users}
                                        badge={
                                            <div className="flex flex-wrap gap-2">
                                                {canAssignShopMember(selectedOrder) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openAssignShopMemberModal(selectedOrder)}
                                                        disabled={loadingAssignmentOptions}
                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50"
                                                    >
                                                        <UserPlus className="mr-1.5" size={14} />
                                                        Assign SM
                                                    </button>
                                                )}
                                                {canReassignShopMember(selectedOrder) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openReassignShopMemberModal(selectedOrder)}
                                                        disabled={loadingAssignmentOptions}
                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 disabled:opacity-50"
                                                    >
                                                        <UserCog className="mr-1.5" size={14} />
                                                        Reassign SM
                                                    </button>
                                                )}
                                            </div>
                                        }
                                    >
                                        {selectedOrder.shopMembers && selectedOrder.shopMembers.length > 0 ? (
                                            <div className="space-y-2">
                                                {selectedOrder.shopMembers.map((member, idx) => (
                                                    <div key={member._id || idx} className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col sm:flex-row items-start gap-3">
                                                        {member.profileImageUrl ? (
                                                            <img src={member.profileImageUrl} alt={member.fullName} className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                                                <User size={18} />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                                <p className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                                                                    {displayText(member.fullName)}
                                                                    {member.isDeleted && (
                                                                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-600">Deleted</span>
                                                                    )}
                                                                </p>
                                                                <StatusPill label={formatStatusLabel(member.status)} tone="success" />
                                                            </div>
                                                            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                                                <ContactLink phone={member.phone} />
                                                                <CopyButton text={member.phone} />
                                                            </div>
                                                            <p className="text-xs text-slate-500 mt-1">{displayText(member.email)}</p>
                                                            <p className="text-xs text-slate-400 mt-1">Confirmed {displayText(formatDate(member.confirmedAt))}</p>
                                                            {member.notes && <p className="text-xs italic text-slate-400 mt-1">“{member.notes}”</p>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <EmptyHint>No shop member assigned yet</EmptyHint>
                                        )}
                                    </DetailSection>
                                </div>
                            </div>

                            <DetailSection
                                title="Delivery Tracking"
                                icon={Truck}
                                badge={
                                    selectedOrder.delivery?.status ? (
                                        <StatusPill label={formatStatusLabel(selectedOrder.delivery.status)} tone="info" />
                                    ) : null
                                }
                            >
                                {selectedOrder.delivery ? (
                                    <div className="space-y-5">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {[
                                                { label: 'Assigned', value: formatDate(selectedOrder.delivery.assignedAt) },
                                                { label: 'Picked up', value: formatDate(selectedOrder.delivery.pickedUpAt) },
                                                { label: 'Delivered', value: formatDate(selectedOrder.delivery.deliveredAt) },
                                                { label: 'Distance', value: selectedOrder.delivery.deliveryDistance != null ? `${selectedOrder.delivery.deliveryDistance} km` : null }
                                            ].map((stat) => (
                                                <div key={stat.label} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
                                                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                                    <p className="text-sm font-semibold text-slate-800 mt-1">{displayText(stat.value)}</p>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <OtpCard title="Pickup OTP" otp={selectedOrder.delivery.pickupOTP} />
                                            <OtpCard title="Delivery OTP" otp={selectedOrder.delivery.deliveryOTP} />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <LocationCard title="Pickup location" location={selectedOrder.delivery.pickupLocation} />
                                            <LocationCard title="Delivery location" location={selectedOrder.delivery.deliveryLocation} />
                                            <LocationCard title="Current location" location={selectedOrder.delivery.currentLocation} />
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Proof of delivery</p>
                                                <ProofOfDeliveryCard proof={selectedOrder.delivery.proofOfDelivery} />
                                            </div>
                                            <div>
                                                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                                    Delivery attempts ({selectedOrder.delivery.deliveryAttempts?.length || 0})
                                                </p>
                                                <DeliveryAttemptsList attempts={selectedOrder.delivery.deliveryAttempts} />
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                                Status timeline ({selectedOrder.delivery.statusHistory?.length || 0})
                                            </p>
                                            {selectedOrder.delivery.statusHistory?.length ? (
                                                <ol className="relative border-l-2 border-slate-200 ml-2">
                                                    {selectedOrder.delivery.statusHistory.map((entry, idx) => (
                                                        <li key={idx} className="relative pl-5 pb-4 last:pb-0">
                                                            <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-sky-500 ring-4 ring-white" />
                                                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                                                <p className="text-sm font-semibold text-slate-800">{formatStatusLabel(entry.status)}</p>
                                                                <p className="text-xs text-slate-400">{displayText(formatDate(entry.timestamp))}</p>
                                                            </div>
                                                            {entry.notes && <p className="text-sm text-slate-500 mt-0.5">{entry.notes}</p>}
                                                        </li>
                                                    ))}
                                                </ol>
                                            ) : (
                                                <EmptyHint>No status updates yet</EmptyHint>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <EmptyHint>No delivery tracking for this order</EmptyHint>
                                )}
                            </DetailSection>
                            </>
                            )}

                            {/* OVERVIEW — items (out-of-stock / unavailable rows hidden) */}
                            {detailTab === 'overview' && (
                            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 sm:p-6">
                                <h3 className="flex items-center text-base font-semibold text-slate-800 mb-4">
                                    <Package className="mr-2 text-sky-700" size={18} />
                                    Order items ({visibleOrderItems.length})
                                </h3>

                                {visibleOrderItems.length > 0 ? (
                                    <>
                                        {/* Mobile card layout */}
                                        <div className="md:hidden border rounded-lg divide-y divide-gray-200 overflow-hidden">
                                            {visibleOrderItems.map((item, index) => (
                                                <div key={item.productId?._id || index} className="p-4 bg-white space-y-3">
                                                    <div className="flex gap-3">
                                                        {(item.productImage || item.productId?.image) && (
                                                            <img
                                                                src={item.productImage || item.productId?.image}
                                                                alt={item.productId?.name || 'Product'}
                                                                className="w-14 h-14 rounded object-cover border border-gray-200 shrink-0"
                                                            />
                                                        )}
                                                        <div className="min-w-0">
                                                            <p className="font-medium text-gray-900 break-words">{displayText(item.productId?.name, 'Product')}</p>
                                                            <p className="text-sm text-gray-500 mt-0.5 break-words">{displayText(item.productId?.description, 'No description')}</p>
                                                            <p className="text-xs text-gray-400 mt-1">Category: {displayText(item.productId?.category?.name)}</p>
                                                            <p className="text-xs text-gray-400 font-mono break-all">Product ID: {displayText(item.productId?._id)}</p>
                                                            <p className="text-xs text-gray-400 font-mono break-all">Shop ID: {displayText(item.shopId || item.shop?._id)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 text-xs">
                                                        <span className="px-2 py-1 bg-gray-100 rounded">Size: {displayText(item.size)}</span>
                                                        <span className="px-2 py-1 bg-gray-100 rounded">Color: {displayText(item.color)}</span>
                                                        <span className="px-2 py-1 bg-gray-100 rounded">Variant: {displayText(item.selectedVariant)}</span>
                                                        <span className="px-2 py-1 bg-gray-100 rounded">HSN: {displayText(item.hsnCode ?? item.productId?.hsnCode)}</span>
                                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">GST: {displayText(item.gstRate, '0')}%</span>
                                                        <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded capitalize">Avail: {displayText(item.availabilityStatus)}</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 text-sm">
                                                        <div><p className="text-xs text-gray-500 uppercase">MRP</p><p>{formatMoney(item.mrp ?? item.productId?.mrp)}</p></div>
                                                        <div><p className="text-xs text-gray-500 uppercase">Price</p><p>{formatMoney(item.price)}</p></div>
                                                        <div><p className="text-xs text-gray-500 uppercase">Qty / Req</p><p>{displayText(item.quantity, '0')} / {displayText(item.requestedQuantity, '—')}</p></div>
                                                        <div><p className="text-xs text-gray-500 uppercase">GST Amt</p><p>{formatMoney(item.gstAmount)}</p></div>
                                                        <div><p className="text-xs text-gray-500 uppercase">Gateway</p><p>{formatMoney(item.paymentGatewayCharges)}</p></div>
                                                        <div><p className="text-xs text-gray-500 uppercase">Commission</p><p>{formatMoney(item.commission)}</p></div>
                                                        <div className="col-span-2"><p className="text-xs text-gray-500 uppercase">Shop Notes</p><p className="text-gray-700">{displayText(item.shopNotes)}</p></div>
                                                        <div className="col-span-2 text-right"><p className="text-xs text-gray-500 uppercase">Line Total</p><p className="font-semibold">{formatMoney(item.totalPrice)}</p></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Desktop table layout */}
                                        <div className="hidden md:block border rounded-lg overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">MRP</th>
                                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">GST</th>
                                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fees</th>
                                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {visibleOrderItems.map((item, index) => (
                                                        <tr key={item.productId?._id || index}>
                                                            <td className="px-3 py-4 align-top">
                                                                <div className="flex gap-3">
                                                                    {(item.productImage || item.productId?.image) && (
                                                                        <img
                                                                            src={item.productImage || item.productId?.image}
                                                                            alt=""
                                                                            className="w-12 h-12 rounded object-cover border border-gray-200 shrink-0"
                                                                        />
                                                                    )}
                                                                    <div className="min-w-0">
                                                                        <div className="font-medium text-gray-900">{displayText(item.productId?.name, 'Product')}</div>
                                                                        <div className="text-sm text-gray-500 mt-1">{displayText(item.productId?.description, 'No description')}</div>
                                                                        <div className="text-xs text-gray-400 mt-1">Category: {displayText(item.productId?.category?.name)}</div>
                                                                        <div className="text-xs text-gray-400 font-mono break-all mt-1">ID: {displayText(item.productId?._id)}</div>
                                                                        <div className="text-xs text-gray-400 font-mono break-all">Shop: {displayText(item.shopId || item.shop?._id)}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-4 align-top">
                                                                <div className="flex flex-wrap gap-1.5 text-xs">
                                                                    <span className="px-2 py-1 bg-gray-100 rounded">Size: {displayText(item.size)}</span>
                                                                    <span className="px-2 py-1 bg-gray-100 rounded">Color: {displayText(item.color)}</span>
                                                                    <span className="px-2 py-1 bg-gray-100 rounded">Variant: {displayText(item.selectedVariant)}</span>
                                                                    <span className="px-2 py-1 bg-gray-100 rounded">HSN: {displayText(item.hsnCode ?? item.productId?.hsnCode)}</span>
                                                                    <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded capitalize">{displayText(item.availabilityStatus)}</span>
                                                                </div>
                                                                <p className="text-xs text-gray-500 mt-2">Notes: {displayText(item.shopNotes)}</p>
                                                            </td>
                                                            <td className="px-3 py-4 whitespace-nowrap text-right text-gray-900 align-top">{formatMoney(item.mrp ?? item.productId?.mrp)}</td>
                                                            <td className="px-3 py-4 whitespace-nowrap text-right text-gray-900 align-top">{formatMoney(item.price)}</td>
                                                            <td className="px-3 py-4 whitespace-nowrap text-right text-gray-900 align-top">
                                                                <div>{displayText(item.quantity, '0')}</div>
                                                                <div className="text-xs text-gray-500">Req: {displayText(item.requestedQuantity)}</div>
                                                            </td>
                                                            <td className="px-3 py-4 whitespace-nowrap text-right text-gray-900 align-top">
                                                                <div>{displayText(item.gstRate, '0')}%</div>
                                                                <div className="text-xs text-gray-500">{formatMoney(item.gstAmount)}</div>
                                                            </td>
                                                            <td className="px-3 py-4 whitespace-nowrap text-right text-gray-900 align-top text-xs">
                                                                <div>GW: {formatMoney(item.paymentGatewayCharges)}</div>
                                                                <div>Comm: {formatMoney(item.commission)}</div>
                                                            </td>
                                                            <td className="px-3 py-4 whitespace-nowrap text-right font-medium text-gray-900 align-top">{formatMoney(item.totalPrice)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                ) : (
                                    <div className="border rounded-lg px-4 py-6 text-center text-slate-400 italic">
                                        No items found in this order
                                    </div>
                                )}
                            </div>
                            )}

                            {/* BILLING TAB */}
                            {detailTab === 'billing' && (
                            <>
                            <div className="space-y-4">
                                <KdpSettlementInvoiceCard
                                    order={selectedOrder}
                                    onView={() => viewShopSettlementInvoice(selectedOrder)}
                                    onDownload={() => downloadShopSettlementInvoice(selectedOrder)}
                                    isViewing={isPreviewingHtml}
                                    isDownloading={isGlobalDownloadingPdf}
                                    canGenerate={canDownloadInvoice(selectedOrder)}
                                />
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <DetailSection
                                        title="Payment"
                                        icon={CreditCard}
                                        badge={
                                            <StatusPill
                                                label={formatStatusLabel(selectedOrder.paymentStatus)}
                                                tone={paymentTone(selectedOrder.paymentStatus)}
                                            />
                                        }
                                    >
                                        <dl>
                                            <MetaRow label="Method" value={displayText(selectedOrder.paymentMethod)} />
                                            <MetaRow label="Payment ID" value={displayText(selectedOrder.paymentId)} mono />
                                            <MetaRow label="Deadline" value={displayText(formatDate(selectedOrder.paymentDeadline))} />
                                        </dl>
                                        <div className="mt-4 pt-3 border-t border-slate-100">
                                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Gateway details</p>
                                            <ObjectFields data={selectedOrder.paymentDetails} emptyLabel="No gateway details" />
                                        </div>
                                    </DetailSection>

                                    <DetailSection
                                        title="Refund"
                                        icon={RefreshCw}
                                        badge={
                                            <StatusPill
                                                label={formatStatusLabel(selectedOrder.refundStatus)}
                                                tone={refundTone(selectedOrder.refundStatus)}
                                            />
                                        }
                                    >
                                        <dl>
                                            <MetaRow label="Reason" value={displayText(selectedOrder.refundReason)} />
                                            <MetaRow label="Stock Restored" value={<BoolText value={selectedOrder.stockRestored} />} />
                                        </dl>
                                        {selectedOrder.refundImage && (
                                            <a
                                                href={selectedOrder.refundImage}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex mt-3 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                                            >
                                                View refund image
                                            </a>
                                        )}
                                        <div className="mt-4 pt-3 border-t border-slate-100">
                                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Refund details</p>
                                            <ObjectFields data={selectedOrder.refundDetails} emptyLabel="No refund details" />
                                        </div>
                                    </DetailSection>

                                    <DetailSection title="Deadlines & Response" icon={Clock}>
                                        <dl>
                                            <MetaRow label="Shop Confirm By" value={displayText(formatDate(selectedOrder.shopConfirmationDeadline))} />
                                            <MetaRow label="User Response By" value={displayText(formatDate(selectedOrder.userResponseDeadline))} />
                                            <MetaRow label="Payment By" value={displayText(formatDate(selectedOrder.paymentDeadline))} />
                                            <MetaRow label="User Accepted" value={<BoolText value={selectedOrder.userResponse?.accepted} />} />
                                            <MetaRow label="Responded At" value={displayText(formatDate(selectedOrder.userResponse?.respondedAt))} />
                                            <MetaRow label="Rejection Reason" value={displayText(selectedOrder.userResponse?.rejectionReason)} />
                                        </dl>
                                    </DetailSection>

                                    <DetailSection title="Coupon" icon={Ticket}>
                                        {selectedOrder.coupon ? (
                                            <dl>
                                                <MetaRow label="Code" value={displayText(selectedOrder.coupon.couponCode || selectedOrder.coupon.code)} />
                                                <MetaRow label="Type" value={formatStatusLabel(selectedOrder.coupon.type || selectedOrder.coupon.discountType)} />
                                                <MetaRow label="Discount" value={displayText(selectedOrder.coupon.discount ?? selectedOrder.coupon.discountValue)} />
                                                <MetaRow label="Max Discount" value={displayText(selectedOrder.coupon.maxDiscount)} />
                                                <MetaRow label="Min / Max Order" value={`${displayText(selectedOrder.coupon.minOrderValue)} / ${displayText(selectedOrder.coupon.maxOrderValue)}`} />
                                                <MetaRow label="Valid" value={`${displayText(formatDate(selectedOrder.coupon.validFrom))} → ${displayText(formatDate(selectedOrder.coupon.validTo))}`} />
                                                <MetaRow label="Active" value={<BoolText value={selectedOrder.coupon.isActive} />} />
                                            </dl>
                                        ) : (
                                            <EmptyHint>No coupon applied</EmptyHint>
                                        )}
                                    </DetailSection>

                                    <DetailSection
                                        title="Review"
                                        icon={Star}
                                        badge={
                                            <StatusPill
                                                label={formatStatusLabel(selectedOrder.reviewStatus)}
                                                tone={selectedOrder.reviewStatus === 'fully_reviewed' ? 'success' : 'neutral'}
                                            />
                                        }
                                    >
                                        {selectedOrder.reviewId?.rating != null ? (
                                            <dl>
                                                <MetaRow label="Rating" value={`${displayText(selectedOrder.reviewId.rating)} / 5`} />
                                                <MetaRow label="Comment" value={displayText(selectedOrder.reviewId.comment)} />
                                                <MetaRow label="Reviewed At" value={displayText(formatDate(selectedOrder.reviewId.createdAt))} />
                                            </dl>
                                        ) : (
                                            <EmptyHint>Customer has not reviewed this order</EmptyHint>
                                        )}
                                    </DetailSection>

                                    <DetailSection title="Technical IDs" icon={Hash}>
                                        <dl>
                                            <MetaRow label="Internal ID" value={<span className="inline-flex items-center gap-2 font-mono text-xs">{displayText(selectedOrder._id)} <CopyButton text={selectedOrder._id} /></span>} />
                                            <MetaRow label="Order ID" value={<span className="inline-flex items-center gap-2 font-mono text-xs">{displayText(selectedOrder.orderId)} <CopyButton text={selectedOrder.orderId} /></span>} />
                                            <MetaRow label="User ID" value={<span className="inline-flex items-center gap-2 font-mono text-xs">{displayText(selectedOrder.userId?._id)} <CopyButton text={selectedOrder.userId?._id} /></span>} />
                                            <MetaRow label="Shop ID" value={<span className="inline-flex items-center gap-2 font-mono text-xs">{displayText(selectedOrder.shopId?._id)} <CopyButton text={selectedOrder.shopId?._id} /></span>} />
                                            <MetaRow label="Created" value={displayText(formatDate(selectedOrder.createdAt))} />
                                            <MetaRow label="Updated" value={displayText(formatDate(selectedOrder.updatedAt))} />
                                        </dl>
                                    </DetailSection>
                                </div>

                            {/* Combined Action Buttons and Order Summary */}
                            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 sm:p-6 flex flex-col items-stretch gap-y-4 sm:flex-row sm:justify-between sm:items-start sm:gap-x-4">
                                {/* Invoice Preview & Download Buttons (now stacked vertically on the left) */}
                                <div className="flex flex-col gap-y-3 flex-1 min-w-0 w-full">
                                    {canCancelAndRefundOrder(selectedOrder) && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                <div>
                                                    <h4 className="text-sm font-medium text-red-800">Payment completed</h4>
                                                    <p className="text-sm text-red-700 mt-1">
                                                        Cancel this order and initiate a Razorpay refund for {formatCurrency(selectedOrder.grandTotal) || 'the order amount'}.
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCancelRefundModal(true)}
                                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                                                >
                                                    <RefreshCw className="mr-2" size={16} />
                                                    Cancel & Refund
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {canRetryRefundOrder(selectedOrder) && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                <div>
                                                    <h4 className="text-sm font-medium text-amber-900">Refund failed</h4>
                                                    <p className="text-sm text-amber-800 mt-1">
                                                        {isInsufficientBalanceFailure(selectedOrder)
                                                            ? 'Razorpay account has insufficient balance. Add funds in the Razorpay dashboard, then retry the refund.'
                                                            : 'The Razorpay refund could not be processed. You can retry after fixing the issue.'}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleRetryRefund}
                                                    disabled={isRetryingRefund}
                                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                                                >
                                                    {isRetryingRefund ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                            Retrying...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <RefreshCw className="mr-2" size={16} />
                                                            Retry Refund
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {['delivered', 'picked'].includes(selectedOrder.status) ? (
                                        <>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-medium text-gray-700 py-2">Preview Invoices (HTML):</span>
                                                <button
                                                    onClick={() => generateInvoice('main', 'html')}
                                                    disabled={isPreviewingHtml}
                                                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isPreviewingHtml ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Receipt className="mr-1" size={16} />}
                                                    Main Invoice
                                                </button>
                                                {selectedOrder.orderType === 'delivery' && selectedOrder.deliveryFee > 0 && (
                                                    <button
                                                        onClick={() => generateInvoice('delivery-fee', 'html')}
                                                        disabled={isPreviewingHtml}
                                                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {isPreviewingHtml ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Receipt className="mr-1" size={16} />}
                                                        Delivery Invoice
                                                    </button>
                                                )}
                                                {selectedOrder.platformFee > 0 && (
                                                    <button
                                                        onClick={() => generateInvoice('platform-fee', 'html')}
                                                        disabled={isPreviewingHtml}
                                                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {isPreviewingHtml ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Receipt className="mr-1" size={16} />}
                                                        Platform Invoice
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => viewShopSettlementInvoice(selectedOrder)}
                                                    disabled={isPreviewingHtml}
                                                    className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isPreviewingHtml ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Receipt className="mr-1" size={16} />}
                                                    KDP Settlement
                                                </button>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-medium text-gray-700 py-2">Download Invoices (PDF):</span>
                                                <button
                                                    onClick={() => generateInvoice('main', 'pdf')}
                                                    disabled={isGlobalDownloadingPdf}
                                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                >
                                                    {isGlobalDownloadingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="inline mr-2" size={16} />}
                                                    Main Invoice
                                                </button>
                                                {selectedOrder.orderType === 'delivery' && selectedOrder.deliveryFee > 0 && (
                                                    <button
                                                        onClick={() => generateInvoice('delivery-fee', 'pdf')}
                                                        disabled={isGlobalDownloadingPdf}
                                                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                    >
                                                        {isGlobalDownloadingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="inline mr-2" size={16} />}
                                                        Delivery Invoice
                                                    </button>
                                                )}
                                                {selectedOrder.platformFee > 0 && (
                                                    <button
                                                        onClick={() => generateInvoice('platform-fee', 'pdf')}
                                                        disabled={isGlobalDownloadingPdf}
                                                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                    >
                                                        {isGlobalDownloadingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="inline mr-2" size={16} />}
                                                        Platform Invoice
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => downloadShopSettlementInvoice(selectedOrder)}
                                                    disabled={isGlobalDownloadingPdf}
                                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#12355B] hover:bg-[#0e2c4a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                >
                                                    {isGlobalDownloadingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="inline mr-2" size={16} />}
                                                    KDP Settlement
                                                </button>
                                            </div>

                                            {/* Info message for pickup orders */}
                                            {selectedOrder.orderType === 'pickup' && (
                                                <div className="text-xs text-gray-500 italic mt-1">
                                                    Note: Delivery invoice is not applicable for pickup orders
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <div className="flex items-center">
                                                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                                                <div>
                                                    <h4 className="text-sm font-medium text-yellow-800">Invoices Not generated</h4>
                                                    <p className="text-sm text-yellow-700 mt-1">
                                                        Invoices generate for delivered or picked up orders. Current status: <span className="font-medium">{statusOptions[selectedOrder.status] || selectedOrder.status?.replace(/_/g, ' ')}</span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Order Summary — full pricing from order model */}
                                <div className="w-full sm:w-auto sm:ml-auto shrink-0">
                                    <div className="space-y-2 w-full sm:min-w-[320px] border border-gray-200 rounded-lg p-4 bg-gray-50">
                                        <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center">
                                            <IndianRupee className="mr-1.5" size={16} />
                                            Pricing Breakdown
                                        </h4>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Original Item Total</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.originalItemTotal)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Updated Item Total</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.updatedItemTotal ?? selectedOrder.itemTotal)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Item GST</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.itemGSTTotal)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Discount</span>
                                            <span className={`font-medium ${selectedOrder.discountAmount > 0 ? 'text-green-600' : ''}`}>
                                                {selectedOrder.discountAmount > 0 ? `- ${formatMoney(selectedOrder.discountAmount)}` : formatMoney(0)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Delivery Fee</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.deliveryFee)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Delivery GST</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.deliveryGSTAmount)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Shipping Charges</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.shippingCharges)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Platform Fee</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.platformFee)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Platform GST</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.platformGSTAmount)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Total GST</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.gstAmount)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 text-sm">
                                            <span className="text-gray-600">Original Grand Total</span>
                                            <span className="font-medium">{formatMoney(selectedOrder.originalGrandTotal)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4 border-t border-slate-200 pt-2 mt-1">
                                            <span className="font-bold text-slate-800">Grand Total</span>
                                            <span className="font-bold text-lg text-slate-900 tabular-nums">{formatMoney(selectedOrder.updatedGrandTotal ?? selectedOrder.grandTotal)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                            </>
                            )}
                        </div>
                    )
                )}

                {showCancelRefundModal && selectedOrder && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold text-gray-900">Cancel order and refund payment?</h3>
                                <p className="text-sm text-gray-600 mt-2">
                                    Order #{formatDisplayOrderId(selectedOrder.orderId)} — refund {formatCurrency(selectedOrder.grandTotal) || 'paid amount'} via Razorpay. This cannot be undone.
                                </p>
                            </div>
                            <div className="px-6 py-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason (optional)
                                </label>
                                <textarea
                                    value={cancelRefundReason}
                                    onChange={(e) => setCancelRefundReason(e.target.value)}
                                    placeholder="Reason for cancellation..."
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (isCancellingRefund) return;
                                        setShowCancelRefundModal(false);
                                        setCancelRefundReason('');
                                    }}
                                    disabled={isCancellingRefund}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancelAndRefund}
                                    disabled={isCancellingRefund}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                >
                                    {isCancellingRefund ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="mr-2 h-4 w-4" />
                                            Cancel & Refund
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Assign Shop Member Modal */}
                {showAssignShopMemberModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold text-gray-900">Assign shop member</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Order #{formatDisplayOrderId(selectedOrder?.orderId)} — {selectedOrder?.shopId?.name}
                                </p>
                            </div>
                            <div className="px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Team member
                                    </label>
                                    <select
                                        value={selectedTeamMemberId}
                                        onChange={(e) => setSelectedTeamMemberId(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                        <option value="">Select shop member</option>
                                        {teamMembersList.map((member) => (
                                            <option key={member._id} value={member._id}>
                                                {member.userId?.fullName || 'Unnamed'} — {member.userId?.phone || 'No phone'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Notes (optional)
                                    </label>
                                    <textarea
                                        value={assignmentNotes}
                                        onChange={(e) => setAssignmentNotes(e.target.value)}
                                        placeholder="Reason for assignment..."
                                        rows={2}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (submittingAssignment) return;
                                        setShowAssignShopMemberModal(false);
                                        setAssignmentNotes('');
                                    }}
                                    disabled={submittingAssignment}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAssignShopMember}
                                    disabled={submittingAssignment || !selectedTeamMemberId}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                >
                                    {submittingAssignment ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Assigning...
                                        </>
                                    ) : (
                                        'Assign'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Reassign Shop Member Modal */}
                {showReassignShopMemberModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold text-gray-900">Reassign shop member</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Order #{formatDisplayOrderId(selectedOrder?.orderId)} — move processing to another team member
                                </p>
                            </div>
                            <div className="px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        New team member
                                    </label>
                                    <select
                                        value={selectedTeamMemberId}
                                        onChange={(e) => setSelectedTeamMemberId(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    >
                                        <option value="">Select shop member</option>
                                        {teamMembersList.map((member) => (
                                            <option key={member._id} value={member._id}>
                                                {member.userId?.fullName || 'Unnamed'} — {member.userId?.phone || 'No phone'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Notes (optional)
                                    </label>
                                    <textarea
                                        value={assignmentNotes}
                                        onChange={(e) => setAssignmentNotes(e.target.value)}
                                        placeholder="Reason for reassignment..."
                                        rows={2}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    />
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (submittingAssignment) return;
                                        setShowReassignShopMemberModal(false);
                                        setAssignmentNotes('');
                                    }}
                                    disabled={submittingAssignment}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleReassignShopMember}
                                    disabled={submittingAssignment || !selectedTeamMemberId}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                                >
                                    {submittingAssignment ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Reassigning...
                                        </>
                                    ) : (
                                        'Reassign'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Assign Delivery Boy Modal */}
                {showAssignDeliveryBoyModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {deliveryBoyModalMode === 'reassign' ? 'Reassign delivery partner' : 'Assign delivery partner'}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Order #{formatDisplayOrderId(selectedOrder?.orderId)}
                                    {selectedOrder?.deliveryFrom === 'KD' && (
                                        <span className="ml-1 text-blue-600">(KD delivery)</span>
                                    )}
                                </p>
                            </div>
                            <div className="px-6 py-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {deliveryBoyModalMode === 'reassign' ? 'New delivery partner' : 'Delivery partner'}
                                </label>
                                <select
                                    value={selectedDeliveryBoyUserId}
                                    onChange={(e) => setSelectedDeliveryBoyUserId(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select delivery partner</option>
                                    {deliveryBoysList.map((boy) => (
                                        <option key={boy.userId} value={boy.userId}>
                                            {boy.fullName || 'Unnamed'} — {boy.phone || 'No phone'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (submittingAssignment) return;
                                        setShowAssignDeliveryBoyModal(false);
                                        setDeliveryBoyModalMode('assign');
                                    }}
                                    disabled={submittingAssignment}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAssignDeliveryBoy}
                                    disabled={submittingAssignment || !selectedDeliveryBoyUserId}
                                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white disabled:opacity-50 ${
                                        deliveryBoyModalMode === 'reassign'
                                            ? 'bg-amber-600 hover:bg-amber-700'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {submittingAssignment ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {deliveryBoyModalMode === 'reassign' ? 'Reassigning...' : 'Assigning...'}
                                        </>
                                    ) : (
                                        deliveryBoyModalMode === 'reassign' ? 'Reassign' : 'Assign'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* HTML Invoice Preview Modal */}
                {isPreviewingHtml && htmlInvoiceContent && (
                    <InvoicePreviewModal
                        htmlContent={htmlInvoiceContent}
                        onClose={handleClosePreview}
                        title={invoicePreviewTitle}
                    />
                )}

                {/* Global Downloading Spinner Overlay */}
                {isGlobalDownloadingPdf && (
                    <div className="fixed inset-0 backdrop-blur-md bg-transparent flex justify-center items-center z-[100]">
                        <div className="flex flex-col items-center text-blue-800">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
                            <p className="text-xl font-semibold">Downloading PDF...</p>
                            <p className="text-sm mt-2 text-blue-600">Please wait, this may take a moment.</p>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default OrderManagementSystem;